import { useRouter } from 'next/router'

import AndroidHandler from 'utils/androidHandler'
import { useAuth } from 'utils/auth'

interface UseBackButtonOptions {
  /**
   * Path to redirect to when there is no browser history or Android handler available.
   * Defaults to the current profile root path (e.g., `/profil/{id}`).
   */
  fallbackPath?: string
}

const PROFILE_ENTRY_PATH_KEY = 'profile_entry_path'

const normalizePath = (path: string) =>
  path
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')
    .trim() || '/'

const getEntryPath = () => {
  if (typeof window === 'undefined') {
    return undefined
  }

  try {
    const storedEntryPath = window.sessionStorage.getItem(
      PROFILE_ENTRY_PATH_KEY
    )

    if (storedEntryPath) {
      return storedEntryPath
    }

    const currentPath = window.location.pathname || ''
    window.sessionStorage.setItem(PROFILE_ENTRY_PATH_KEY, currentPath)
    return currentPath
  } catch {
    return undefined
  }
}

/**
 * Provides a consistent back button handler for profile-related screens across web and native shells.
 *
 * Behavior priority:
 * 1. Detect the very first path loaded in the browser/WebView (e.g., `/profil/{id}`, `/profil/{id}/bukti-karya`).
 *    When the current path matches that stored entry path, trigger `AndroidHandler.backToApp()` (when available)
 *    so the native container can close the WebView. If the entry path cannot be read (private mode, SSR, etc.),
 *    fall back to the decoded `/profil/{id}` route for safety.
 * 2. If the browser history stack contains previous entries, invoke `router.back()` to respect the
 *    client-side navigation trail for regular web usage.
 * 3. Otherwise, replace the current route with the provided fallback path (defaults to `/profil/{id}` unless overridden).
 */
export const useBackButton = (options?: UseBackButtonOptions) => {
  const { session } = useAuth()
  const router = useRouter()
  const androidHandler = AndroidHandler()

  const profileId = decodeURI(
    (router.query.id ?? session?.user?.id ?? '') as string
  ).trim()

  const entryPath = getEntryPath()
  const normalizedEntryPath = entryPath ? normalizePath(entryPath) : undefined
  const profileRootPath = `/profil/${profileId}`
  const fallbackPath = options?.fallbackPath ?? profileRootPath

  const hasBrowserHistory = () =>
    typeof window !== 'undefined' && window.history.length > 1

  const onClickBack = () => {
    const normalizedCurrentPath = normalizePath(router.asPath)

    const isOnEntryPath = normalizedEntryPath
      ? normalizedCurrentPath === normalizedEntryPath
      : normalizedCurrentPath === normalizePath(profileRootPath)

    if (androidHandler.enabled && isOnEntryPath) {
      androidHandler.backToApp()
      return
    }

    if (hasBrowserHistory()) {
      router.back()
      return
    }

    router.replace(fallbackPath)
  }

  return { onClickBack }
}
